/**
 * UAPC Anytime - Main Application Component
 * @module App
 * @author Mohammed Qamrain Shaikh - Advanced Full Stack Developer
 * @author Osman - Frontend Developer
 * @description Main React application with routing
 * @version 1.0.0
 * Git: Mohammed Qamrain Shaikh <mohammed@linesoft.com> - App architecture
 * Git: Osman <osman@linesoft.com> - Route configuration
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import SearchPage from './pages/SearchPage';
import PartDetailPage from './pages/PartDetailPage';
import CartPage from './pages/CartPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import VendorDashboard from './pages/vendor/VendorDashboard';
import VendorInventory from './pages/vendor/VendorInventory';
import VendorOrders from './pages/vendor/VendorOrders';
import VendorReports from './pages/vendor/VendorReports';
import VendorSync from './pages/vendor/VendorSync';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminVendors from './pages/admin/AdminVendors';
import AdminReports from './pages/admin/AdminReports';
import AdminCommissions from './pages/admin/AdminCommissions';
import FavoritesPage from './pages/FavoritesPage';
import OrdersPage from './pages/OrdersPage';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Vendor Route
const VendorRoute = ({ children }) => {
  const { user } = useAuth();
  
  if (user?.role === 'vendor' && !user?.is_approved) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="text-center p-8">
          <h2 className="font-heading text-2xl font-bold text-slate-900 dark:text-white mb-4">
            ACCOUNT PENDING APPROVAL
          </h2>
          <p className="text-slate-600 dark:text-slate-400">
            Your vendor account is awaiting admin approval. Please check back later.
          </p>
        </div>
      </div>
    );
  }
  
  return <ProtectedRoute allowedRoles={['vendor', 'admin']}>{children}</ProtectedRoute>;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <Toaster 
            position="top-right" 
            richColors 
            toastOptions={{
              style: {
                fontFamily: 'Manrope, sans-serif',
              },
            }}
          />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/parts/:partId" element={<PartDetailPage />} />
            
            {/* Authenticated Routes */}
            <Route path="/cart" element={
              <ProtectedRoute><CartPage /></ProtectedRoute>
            } />
            <Route path="/checkout/success" element={
              <ProtectedRoute><CheckoutSuccessPage /></ProtectedRoute>
            } />
            <Route path="/favorites" element={
              <ProtectedRoute><FavoritesPage /></ProtectedRoute>
            } />
            <Route path="/orders" element={
              <ProtectedRoute><OrdersPage /></ProtectedRoute>
            } />
            
            {/* Vendor Routes */}
            <Route path="/vendor" element={
              <VendorRoute><VendorDashboard /></VendorRoute>
            } />
            <Route path="/vendor/inventory" element={
              <VendorRoute><VendorInventory /></VendorRoute>
            } />
            <Route path="/vendor/orders" element={
              <VendorRoute><VendorOrders /></VendorRoute>
            } />
            <Route path="/vendor/reports" element={
              <VendorRoute><VendorReports /></VendorRoute>
            } />
            <Route path="/vendor/sync" element={
              <VendorRoute><VendorSync /></VendorRoute>
            } />
            
            {/* Admin Routes */}
            <Route path="/admin" element={
              <ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>
            } />
            <Route path="/admin/vendors" element={
              <ProtectedRoute allowedRoles={['admin']}><AdminVendors /></ProtectedRoute>
            } />
            <Route path="/admin/reports" element={
              <ProtectedRoute allowedRoles={['admin']}><AdminReports /></ProtectedRoute>
            } />
            <Route path="/admin/commissions" element={
              <ProtectedRoute allowedRoles={['admin']}><AdminCommissions /></ProtectedRoute>
            } />
            
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
